import { FC } from 'react';
import {
  DefaultValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { chakra, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import * as Layout from 'layouts/StickyStepperLayout';

import locales from './locales';
import { setupBudgetCardFormSchema, SetupBudgetCardFormValues } from './schema';

interface SetupBudgetCardFormProps {
  onSubmit: SubmitHandler<SetupBudgetCardFormValues>;
  defaultValues?: DefaultValues<SetupBudgetCardFormValues>;
}

export const SetupBudgetCardForm: FC<SetupBudgetCardFormProps> = ({
  defaultValues,
  onSubmit,
}) => {
  const form = useForm<SetupBudgetCardFormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(setupBudgetCardFormSchema),
  });

  const { isSubmitting, isValid } = form.formState;

  return (
    <FormProvider {...form}>
      <chakra.form display="contents" onSubmit={form.handleSubmit(onSubmit)}>
        <Layout.ContentsContainer gap="space-40">
          <VStack align="flex-start" spacing="space-16">
            <Heading size="lg">{locales.title}</Heading>
            <Text>{locales.description}</Text>
          </VStack>
          <VStack alignItems="stretch" spacing="space-24">
            <VStack alignItems="stretch" spacing="space-24">
              <Form.Field
                error={form.formState.errors.amount?.message}
                hint={locales.fields.amount.hint}
                label={locales.fields.amount.label}
              >
                <Form.AmountInput
                  placeholder={locales.fields.amount.placeholder}
                  {...form.register('amount', {
                    valueAsNumber: true,
                  })}
                />
              </Form.Field>
              <Form.Field
                error={form.formState.errors.description?.message}
                label={locales.fields.description.label}
              >
                <Form.Input
                  placeholder={locales.fields.description.placeholder}
                  {...form.register('description')}
                />
              </Form.Field>
            </VStack>
          </VStack>
        </Layout.ContentsContainer>
        <Layout.FooterContainer>
          <StepperProgressBar />

          <Layout.FooterButtonsContainer>
            <Button
              icon="send"
              iconPosition="right"
              isDisabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
              type="submit"
            >
              {locales.submit}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </chakra.form>
    </FormProvider>
  );
};
